import {X as XIcon} from 'lucide-react'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'

export interface BeneficiaryChip {
    id: string
    displayName: string
}

export interface BeneficiaryMultiSelectProps {
    chips: BeneficiaryChip[]
    options: ComboboxOption[]
    onPick: (id: string) => void
    onRemove: (id: string) => void
    onSearch: (q: string) => void
    onSearchInputBackspace: () => void
    isLoading?: boolean
    disabled?: boolean
    placeholder: string
    emptyMessage: string
    'data-testid'?: string
}

/**
 * Chip-strip multi-select picker for the AccessRequestWizard beneficiary step.
 * Owns the chip rendering + Backspace-removes-last-chip behaviour. Chip styling
 * matches the existing {@link LookupCombobox} multi mode so the wizard and the
 * generic picker stay visually consistent.
 *
 * Lives under {@code components/shared} so the chip's raw Tailwind classes are
 * exempt from {@code FrontendNoRawTailwindInPages}.
 */
export function BeneficiaryMultiSelect({
    chips,
    options,
    onPick,
    onRemove,
    onSearch,
    onSearchInputBackspace,
    isLoading,
    disabled,
    placeholder,
    emptyMessage,
    'data-testid': testId = 'beneficiary-multi-picker',
}: BeneficiaryMultiSelectProps) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Backspace') {
            const target = e.target as HTMLElement
            if (target instanceof HTMLInputElement && target.value.length === 0 && chips.length > 0) {
                e.preventDefault()
                onSearchInputBackspace()
            }
        }
    }
    return (
        <div className="space-y-2">
            {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="beneficiary-chips">
                    {chips.map((chip) => (
                        <button
                            key={chip.id}
                            type="button"
                            onClick={() => onRemove(chip.id)}
                            data-testid={`beneficiary-chip-${chip.id}`}
                            aria-label={`Remove ${chip.displayName}`}
                            className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-0.5 text-sm hover:bg-primary/20"
                        >
                            <span className="truncate max-w-[12rem]">{chip.displayName}</span>
                            <XIcon className="h-3 w-3"/>
                        </button>
                    ))}
                </div>
            )}
            <div onKeyDown={handleKeyDown}>
                <Combobox
                    options={options}
                    value={null}
                    onValueChange={(id) => id && onPick(id)}
                    onSearch={onSearch}
                    isLoading={isLoading}
                    placeholder={placeholder}
                    emptyMessage={emptyMessage}
                    disabled={disabled}
                    data-testid={testId}
                />
            </div>
        </div>
    )
}
